"use client";
import Loader from "../Loader";
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useAppSelector,
  useAppDispatch,
} from "@/utils/hooks/reactUseCustomHooks";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { EventEmitter } from "events";
import { getCookie } from "cookies-next";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { RootState } from "@/utils/redux/store";
import { addOrUpdateListItemsToCart } from "@/utils/redux/shoppingList/actions";
import { renameCustomMangementList, setCustomManageList } from "@/utils/redux/shoppingList";
import { setAlert } from "@/utils/redux/gigya/scene";
import { Box, Text, Toast } from "@dxp-feed/ui";
import Link from "next/link";
import useWindowSize from "@/utils/hooks/useWindowSize";

export const flyerEvents = new EventEmitter();

declare global {
  interface Window {
    Flipp: any;
  }
}

interface PublicationInfo {
  element: HTMLElement;
  id: string;
  title?: string;
  dateRange?: string;
}

interface PrintFlyer {
  storeId: string;
  publicationId?: string;
  key?: string;
}
type ToastType = "addList" | "removeList" | "";
localStorage.setItem("flyerdeleting", "[]")
export default function PrintFlyer({ storeId, publicationId }: PrintFlyer) {
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const [initialized, setInitialized] = useState(false);
  const { loggedIn: userLoggedIn, uid: Uid } = useAppSelector(
    (state) => state.gigyaslice
  );
  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );
  const favouriteList: any = useAppSelector(
    (state) => state?.favouriteProductSlice
  );
  const userData = useAppSelector(
    (state: RootState) => state.manageListSlice.userData
  );
  const shoppingList: any = useAppSelector((state) => state?.shoppingListSlice);

  // Login modal state
  // const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [productToBeAdded, setProductToBeAdded] = useState<any>(null);
  const [productToBeDeleted, setProductToBeDeleted] = useState<any>(null);

  const [quantity, setQuantity] = useState(1);

  // const handleLoginModal = () => {
  //   setLoginModalOpen((prev) => !prev);
  //   setShowQuantityText(false);
  // };
  // const handleListResponse = () => setLoginModalOpen(false);
  const [showQuantityText, setShowQuantityText] = useState(false);
  const recipeName = "";

  const flippContainerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const publicationsRef = useRef<PublicationInfo[]>([]);
  const { width } = useWindowSize();
  // Add a map for faster lookups
  const publicationMapRef = useRef<Record<string, PublicationInfo>>({});
  const mainIframeRef = useRef<HTMLIFrameElement | null>(null);
  const mainAsideIframeRef = useRef<HTMLIFrameElement | null>(null);
  const currentPublicationId = useRef<string | null>(null);
  const previousStoreId = useRef<string | null>(null);

  // Add a ref to track previous publication ID
  const previousPublicationId = useRef<string | null>(null);

  const gigyaToken = useGigyaAccountInfo();
  const getToken = async () => {
    const result = await gigyaToken();
    return result?.idToken ?? "";
  };
  const [toastMessage, setToastMessage] = useState<ToastType>("");
  const [isOpen, setIsOpen] = useState(false);
  const openDialog = (type: ToastType) => {
    setToastMessage(type);
    setIsOpen(true);
  };
  const closeDialog = () => setIsOpen(false);
  useEffect(() => {
    if (previousStoreId.current && previousStoreId.current !== storeId) {
      resetState();
    }

    previousStoreId.current = storeId;
  }, [storeId]);

  // Reset all state and refs
  const resetState = useCallback(() => {
    publicationsRef.current = [];
    publicationMapRef.current = {};
    currentPublicationId.current = null;
    previousPublicationId.current = null;
    setInitialized(false);
    mainIframeRef.current = null;
    mainAsideIframeRef.current = null
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const selectPublicationMobile = useCallback((pubId: string, container: any) => {
    const iframeData = container.querySelector('.flippiframe.mainframe');
    if (!iframeData) return false;

    const contentDoc = iframeData.contentDocument || iframeData.contentWindow?.document;
    if (!contentDoc) return false;
    try {
      const element = contentDoc.querySelector(
        `flipp-publication[publication-id="${pubId}"] .flipp-publication-button`
      );
      if (element) {
        setLoading(true)
        const button = element as HTMLElement;
        requestAnimationFrame(() => {
          button.click();
        });
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setLoading(false);
    }
  }, [flippContainerRef, publicationId]);

  useEffect(() => {
    const storeFlyer = JSON.parse(localStorage.getItem("flyerdeleting") || "[]")
    if (!storeFlyer?.length) return
    const asideDoc = mainAsideIframeRef?.current?.contentDocument;
  }, [mainAsideIframeRef])

  useEffect(() => {
    const handleFlyerChanged = (newPublicationId: string) => {
      if (flippContainerRef.current) {
        if (width >= 768) {
          selectPublication(newPublicationId);
        } else {
          selectPublicationMobile(
            newPublicationId,
            flippContainerRef.current
          );
        }
      }
      setLoading(false);
    };
    const updateFlyerButton = (productId: string) => {
      const storeFlyer = JSON.parse(localStorage.getItem("flyerdeleting") || "[]")
      const asideDoc = mainAsideIframeRef?.current?.contentDocument;
      const flyerProduct = asideDoc?.querySelector(
        `flipp-product-page[product-id="${productId}"]`
      );
      if (!flyerProduct) {
        storeFlyer.push(productId)
        localStorage.setItem("flyerdeleting", JSON.stringify(storeFlyer))
        return
      }
      const button = flyerProduct.querySelector(
        "flipp-shopping-list-button button.shopping-list-default-button"
      );
      if (!button) return;
      const savedList = JSON.parse(
        localStorage.getItem("flyerItems") || "[]"
      );
      const isAdded = savedList.includes(productId);

      const translation = button.querySelector(
        "flipp-translation"
      ) as HTMLElement;
      if (translation && isAdded) {
        translation.click();
      }
    };
    flyerEvents.on("flyerChanged", handleFlyerChanged);
    flyerEvents.on("flyerload", updateFlyerButton);

    return () => {
      flyerEvents.off("flyerChanged", handleFlyerChanged);
      flyerEvents.off("flyerload", updateFlyerButton);
    };
  }, [width, mainAsideIframeRef]);

  // Select a publication by ID with improved caching
  const selectPublication = useCallback((pubId: string): boolean => {
    if (!flippContainerRef.current) {
      return false;
    }
    const container = flippContainerRef.current;
    const iframeData = container.querySelector('iframe');
    if (!iframeData) return false;
    const contentDoc = iframeData.contentDocument || iframeData.contentWindow?.document;
    if (!contentDoc) return false;
    try {
      const element = contentDoc.querySelector(
        `flipp-filmstrip-pub[publication-id="${pubId}"]`
      ) as HTMLElement;
      if (element) {
        const button = element as HTMLElement;

        requestAnimationFrame(() => {
          button.click();
        });

        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      setLoading(false);
    }
  }, [flippContainerRef, publicationId]);

  // Track page view analytics
  const triggerFlyerPageViewedEvent = useCallback(() => {
    sendSalesforceInteraction({
      name: "flyersViewed",
      banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      isAnonymous: Uid ? 0 : 1,
      gigyaUID: Uid,
      screenClass: `${process.env.NEXT_PUBLIC_BASE_URL}/flyer`,
      screenName: "flyer",
      utmSource: getUTMParameter("utm_source"),
      utmMedium: getUTMParameter("utm_medium"),
      utmCampaign: getUTMParameter("utm_campaign"),
    });
  }, [Uid]);

  // Handle iframe load
  const handleIframeLoad = useCallback((iframe: HTMLIFrameElement) => {
    try {
      if (!iframe) return;
      if (iframe.contentDocument?.readyState === 'complete') {
        mainIframeRef.current = iframe;
        if (mainIframeRef.current && publicationId) {
          if (width >= 768) {
            selectPublication(publicationId);
          } else {
            selectPublicationMobile(publicationId, flippContainerRef.current);
          }
        }
      }
    } catch (err) {
      console.log("something went wront", err);
    } finally {
      setLoading(false)
    }
  }, [publicationId, flippContainerRef]);

  const addProducts = async () => {
    const localItemId = Date.now().toString();

    let token = await getToken();

    if (!userLoggedIn) {
      let storedItems = JSON.parse(localStorage.getItem("flyerItems") || "[]");
      const { meta } = productToBeAdded;
      const { item_Id } = JSON.parse(meta);
      storedItems.push(item_Id);
      localStorage.setItem("flyerItems", JSON.stringify(storedItems));
      openDialog("addList");
      dispatch(
        setCustomManageList({
          items: [productToBeAdded],
        })
      );
    } else {

      const payload = {
        items: [productToBeAdded],
        storeId: storeId,
        userToken: token,
      };

      try {
        const response: any = await dispatch(addOrUpdateListItemsToCart(payload));

        let storedItems = JSON.parse(localStorage.getItem("flyerItems") || "[]");
        const { item_Id } = JSON.parse(response?.payload?.data[0].meta)
        if (response?.meta?.requestStatus == "fulfilled") {
          storedItems.push(item_Id);
          localStorage.setItem("flyerItems", JSON.stringify(storedItems));
          openDialog("addList");
        }
      } catch (error) {
        console.error("addListItemsToCart failed:", error);
        dispatch(
          setAlert({
            message: "An error occurred while adding the product. Please try again.",
            color: "error",
            isError: true,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (productToBeAdded) addProducts();
  }, [productToBeAdded, userLoggedIn]);

  const logOnListAdd = async (item: any) => {
    let token = await getToken();
    const meta = JSON.stringify({
      name: item?.name || " ",
      item_Id: item?.itemId
        ? item?.itemId.toString()
        : !userLoggedIn
          ? Date.now().toString()
          : "",
      itemType: item?.itemType,
      category: item?.category
    });
    const data = {
      meta,
      item_id: !userLoggedIn ? `${item?.itemId}` : "0",
      quantity: 1,
      product_type: "Custom",
    };
    setProductToBeAdded(data);
    return true;
  };

  const logOnListRemove = async (item: any) => {
    const meta = JSON.stringify({
      name: item.name || " ",
      item_Id: item?.itemId
        ? item?.itemId.toString()
        : !userLoggedIn
          ? Date.now().toString()
          : "",
      itemType: item?.itemType,
      category: item?.category
    });
    const data = {
      meta,
      item_id: !userLoggedIn ? item?.itemId : "0",
      quantity: 0,
      product_type: "Custom",
    };
    setProductToBeDeleted(data)
    return true;
  }

  const deleteProduct = async () => {
    let storedItems = JSON.parse(localStorage.getItem("flyerItems") || "[]");
    if (!userLoggedIn) {
      dispatch(renameCustomMangementList({
        items: [productToBeDeleted]
      }));
      const { meta } = productToBeDeleted
      const { item_Id } = JSON.parse(meta)
      const itemssss = storedItems.filter((i: any) => i != item_Id)
      localStorage.setItem("flyerItems", JSON.stringify(itemssss));
      openDialog("removeList");
    }
    else {
      const userResponse = await gigyaToken();
      const payload = {
        items: [productToBeDeleted], storeId: storeId, userToken: userResponse?.idToken
      };
      if (userResponse.idToken) {
        try {
          await dispatch(addOrUpdateListItemsToCart(payload));
          const { meta } = productToBeDeleted
          const { item_Id } = JSON.parse(meta)
          const itemssss = storedItems.filter((i: any) => i != item_Id)
          localStorage.setItem("flyerItems", JSON.stringify(itemssss));
          openDialog("removeList");
        } catch (error) {
          console.error("removeListItemsToCart failed:", error);
        }
      }
    }
  }

  useEffect(() => {
    if (productToBeDeleted) deleteProduct();
  }, [productToBeDeleted])

  function onGetItems() {
    const itemData: any = localStorage.getItem("flyerItems")
    const flyerItems = JSON.parse(itemData)
    return { items: flyerItems?.map((i: any) => i) };
  }

  const initializeFlipp = useCallback(() => {
    const container = flippContainerRef.current;
    if (!container || !window.Flipp) return;
    const initialPublicationId = previousPublicationId.current;
    const config = {
      accessToken: process.env.NEXT_PUBLIC_FLYER_ACCESS_TOKEN,
      merchantId: process.env.NEXT_PUBLIC_MERCHANT_ID_SOBEYS,
      merchantNameIdentifier: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      storeCode: storeId,
      locale: process.env.NEXT_PUBLIC_LOCALE,
      publicationId: previousPublicationId.current,
      flyerId: previousPublicationId.current,
      configUrl: process.env.NEXT_PUBLIC_IFRAME_CONFIG_URL ?? "",
      shoppingListDelegate: {
        onListAdd: logOnListAdd,
        onListRemove: logOnListRemove,
        getItems: onGetItems,
      },
    };

    currentPublicationId.current = config.publicationId;
    previousPublicationId.current = config.publicationId;

    const storefront = new window.Flipp.Storefront(config);
    applyUserCookieSettings(storefront);
    storefront.renderStorefront(container);
    triggerFlyerPageViewedEvent();

    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;
        const iframe = container.querySelector("iframe");
        const aside = container.querySelector("aside");
        const asideIframe = aside?.querySelector("iframe");
        if (asideIframe) {
          mainAsideIframeRef.current = asideIframe;
        }
        if (iframe) {
          iframe.addEventListener("load", () => {
            handleIframeLoad(iframe);

            if (iframe?.contentWindow) {
              iframe.contentWindow.addEventListener("click", (event) => {
                const eventTarget = event.target as HTMLElement;
                if (!eventTarget) {
                  return;
                }
                const publicationElem = eventTarget?.closest(
                  "flipp-filmstrip-pub"
                ) as HTMLElement;
                if (publicationElem) {
                  const pubId = publicationElem?.getAttribute("publication-id");
                  if (pubId) {
                    flyerEvents.emit("flyerSelectionChanged", pubId);
                  }
                }
              });
            }
          });

          observerRef.current?.disconnect();
        }
      }
    });
    observerRef.current.observe(container, { childList: true, subtree: true });
  }, [storeId, publicationId]);

  const applyUserCookieSettings = (storefront: any) => {
    const cookie: any = getCookie("OptanonConsent");
    if (!cookie) return storefront.setUserAcceptedCookies([]);
    const match = cookie.match(/groups=([^&]*)/);
    const groupString = match?.[1];
    if (!groupString) return storefront.setUserAcceptedCookies([]);

    const groups = Object.fromEntries(
      groupString.split(",").map((item: any) => item.split(":"))
    );
    const acceptedCookies = [];
    if (parseInt(groups["C0002"]) == 1)
      acceptedCookies.push(window.Flipp.CookieCategory.PERFORMANCE);
    if (parseInt(groups["C0003"]) == 1)
      acceptedCookies.push(window.Flipp.CookieCategory.FUNCTIONAL);
    storefront.setUserAcceptedCookies(acceptedCookies);
  };

  // Load Flipp script and init on mount
  useEffect(() => {
    const scriptUrl = process.env.NEXT_PUBLIC_FLIP_FLYER_JS_URL!;
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

    if (existingScript) {
      initializeFlipp();
      return;
    }
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.setAttribute("type", "text/javascript");
    script.setAttribute("data-ot-ignore", "true");
    script.onload = () => initializeFlipp();
    document.head.appendChild(script);

    return () => {
      observerRef.current?.disconnect();
      document.head.removeChild(script);
      resetState();
    };
  }, [publicationId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) {
      timer = setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <>
      <div>
        <div className="main-content">
          {loading && (
            <div className="flex items-center justify-center min-h-[250px]">
              <Loader />
            </div>
          )}
          <div ref={flippContainerRef}></div>
        </div>

      </div>
      {isOpen && (
        <Toast
          message={
            <Box
              as="div"
              className="flex flex-col md:justify-between md:flex-row items-start p-0 gap-[10px]"
            >
              <Text
                as="p"
                className="text-body text-brandBlack !font-light pb-[0px]"
              >
                <Text as="span" className="!font-normal">
                  {toastMessage === "addList"
                    ? "Flyer item added to your shopping list."
                    : "Flyer item removed from shopping list."}
                </Text>
              </Text>
              {toastMessage === "addList" && (
                <Link
                  href="/shopping-list"
                  className="block ml-[0px] font-bold text-primary600 text-body underline hover:no-underline"
                >
                  View List
                </Link>
              )}
            </Box>
          }
          onClose={() => { }}
          position="top"
          autoCloseDuration={3000}
          closeButton={
            <span
              onClick={closeDialog}
              className={`icon-close text-brandBlack font-bold ml-3 cursor-pointer`}
            />
          }
          className={`max-w-[90%] md:max-w-[480px] w-[100%] outline-none bg-primary100 top-[150px]`}
        />
      )}
    </>

  );
}
